import React, {useState} from 'react';
import {Box, styled} from '@mui/material';
import {Icon} from '@lightricks/react-design-system';
import CheckboxMui from '../checkbox-mui';
import FormGroup from '../form-group';
import ButtonMui from '../button-mui';
import FilterRangeSlider from '../filter-range-slider';
import {FilterState} from '@/stores/gigsStore';
import ToggleButtonMui from '../toggle-button';
import translate from '@/utils/translate';
import Stack from '@/components/stack';
import ResponsiveStack from '@/components/responsive-stack';
import Conditional from '@/components/conditional';
import {VerticalTag} from '@/types/verticalTag';

const DEFAULT_MIN_SOCIAL_FOLLOWERS = 0;
const DEFAULT_MAX_SOCIAL_FOLLOWERS = 100000;
const DEFAULT_MIN_COMPENSATION_SUM = 0;
const DEFAULT_MAX_COMPENSATION_SUM = 5000;

const DELIVERABLES_LIST = [
  {
    id: 'photo',
    value: 'photo',
    labelLocaleKey: 'Photo',
    dataKey: 'photos',
  },
  {
    id: 'video',
    value: 'video',
    labelLocaleKey: 'Video',
    dataKey: 'videos',
  },
];

const COMPENSATION_CHECKBOXES_LIST = [
  {
    id: 'fixed',
    value: 'fixed',
    labelLocaleKey: 'Fixed',
    dataKey: 'fixed',
  },
  {
    id: 'negotiable',
    value: 'negotiable',
    labelLocaleKey: 'Negotiable',
    dataKey: 'negotiable',
  },
];

type SocialNetworkProp = {
  name: string;
  icon?: React.FC;
  titleLocaleKey?: string;
};
const NETWORKS_LIST: SocialNetworkProp[] = [
  {
    name: 'facebook',
    icon: () => (
      <Icon name="Social-Facebook" size="medium" appearance="neutral" />
    ),
  },
  {
    name: 'instagram',
    icon: () => (
      <Icon name="Social-Instagram" size="medium" appearance="neutral" />
    ),
  },
  {
    name: 'youtube',
    icon: () => (
      <Icon name="Social-YouTube" size="medium" appearance="neutral" />
    ),
  },
  {
    name: 'pinterest',
    icon: () => (
      <Icon name="Social-Pinterest" size="medium" appearance="neutral" />
    ),
  },
  {
    name: encodeURIComponent('tiktok_creator_marketplace,tiktok_v2'),
    icon: () => (
      <Icon name="Social-TikTok" size="medium" appearance="neutral" />
    ),
  },
  {
    name: 'content_creation',
    titleLocaleKey: 'Content Creation',
  },
];

// Styled components

const SectionWithBorder = styled(FormGroup)(() => ({
  borderBottom: '1px solid',
  paddingBottom: '36px',
}));

const SectionHeader = styled(Box)(() => ({
  marginTop: '35px',
  marginBottom: '24px',
}));

const SectionHeaderTitle = styled('span')(() => ({
  fontSize: '20px',
  color: 'rgba(0, 0, 0, 0.6)',
  fontWeight: 700,
  height: '23px',
  display: 'block',
}));

const SectionHeaderSubtitle = styled('p')(() => ({
  fontSize: '16px',
  color: 'rgba(0, 0, 0, 0.6)',
  fontWeight: 300,
  lineHeight: '18px',
  marginBottom: '0',
}));

const SectionWithoutBottomMargin = styled(FormGroup)(() => ({
  paddingBottom: '12px',
}));

const SectionTagsBase = styled(Box)(() => ({
  marginTop: '-4px',
}));

const shouldNotForwardProps = ['isOpen'];
const SectionTagsContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{isOpen?: boolean}>(({isOpen}) => ({
  height: isOpen ? 'auto' : '220px',
  overflow: 'hidden',
}));

const SectionShowMoreButton = styled(Stack)(() => ({
  marginTop: '12px',
}));

const SocialNetworksStack = styled(Stack)(() => ({
  flexWrap: 'wrap',
  gap: '16px',
}));

type UpdateFilterFunction = (
  key: keyof FilterState,
  value: FilterState[keyof FilterState]
) => void;

interface GigsFiltersPopupCanvasProps {
  updateFilters: UpdateFilterFunction;
  filters: FilterState;
  verticalTags: VerticalTag[];
  isLoadingVerticalTags: boolean;
}

// Define basic types based on usage
interface CheckboxItem {
  id: string | number;
  value: string;
  labelLocaleKey: string;
  dataKey: string;
}

function GigsFiltersPopupCanvas({
  updateFilters,
  filters,
  verticalTags,
  isLoadingVerticalTags,
}: GigsFiltersPopupCanvasProps) {
  const [isVerticalTagsOpen, setIsVerticalTagsOpen] = useState(false);

  return (
    <>
      <SectionWithBorder>
        <SectionHeader>
          <SectionHeaderTitle>{translate('Deliverables')}</SectionHeaderTitle>
        </SectionHeader>
        <ResponsiveStack>
          {DELIVERABLES_LIST.map((item: CheckboxItem) => (
            <CheckboxMui
              testID={item.dataKey}
              key={item.id}
              value={item.value}
              label={translate(item.labelLocaleKey)}
              checked={
                filters?.deliverables?.[
                  item.dataKey as keyof typeof filters.deliverables
                ]
              }
              onChange={(event) => {
                const value = {
                  ...(filters?.deliverables || {}),
                  [item.dataKey]: event.target.checked,
                };
                updateFilters('deliverables', value);
              }}
            />
          ))}
        </ResponsiveStack>
      </SectionWithBorder>

      <SectionWithBorder>
        <SectionHeader>
          <SectionHeaderTitle>
            {translate('Social Networks')}
          </SectionHeaderTitle>
          <SectionHeaderSubtitle>
            {translate(
              "To apply for a gig on a specific platform, you'll first need to connect your account."
            )}
          </SectionHeaderSubtitle>
        </SectionHeader>

        <SocialNetworksStack direction="row">
          {NETWORKS_LIST.map((network) => (
            <ToggleButtonMui
              testID={`network-toggle-${network.name}`}
              type="socials"
              key={network.name}
              value={network.name}
              selected={filters?.socialNetworks?.includes(network.name)}
              onChange={(event, key) => {
                const value = filters?.socialNetworks?.includes(network.name)
                  ? filters?.socialNetworks?.filter(
                      (item: string) => item !== network.name
                    )
                  : [...(filters?.socialNetworks || []), network.name];

                updateFilters('socialNetworks', value);
              }}
            >
              {network.icon && <network.icon />}
              {network.titleLocaleKey && (
                <span>{translate(network.titleLocaleKey)}</span>
              )}
            </ToggleButtonMui>
          ))}
        </SocialNetworksStack>

        <Stack direction="column">
          <FilterRangeSlider
            showOnMax
            maxSign="+"
            label={translate('Number of followers')}
            name="followers"
            curMin={
              filters.socialFollowersRange?.curMin ||
              DEFAULT_MIN_SOCIAL_FOLLOWERS
            }
            curMax={
              filters.socialFollowersRange?.curMax ||
              DEFAULT_MAX_SOCIAL_FOLLOWERS
            }
            value={[
              filters.socialFollowersRange?.curMin ||
                DEFAULT_MIN_SOCIAL_FOLLOWERS,
              filters.socialFollowersRange?.curMax ||
                DEFAULT_MAX_SOCIAL_FOLLOWERS,
            ]}
            min={
              filters.socialFollowersRange?.min || DEFAULT_MIN_SOCIAL_FOLLOWERS
            }
            max={
              filters.socialFollowersRange?.max || DEFAULT_MAX_SOCIAL_FOLLOWERS
            }
            onChange={(minMaxObject) => {
              updateFilters('socialFollowersRange', minMaxObject);
            }}
          />
        </Stack>
      </SectionWithBorder>

      <SectionWithBorder>
        <SectionHeader>
          <SectionHeaderTitle>{translate('Compensation')}</SectionHeaderTitle>
        </SectionHeader>
        <ResponsiveStack>
          {COMPENSATION_CHECKBOXES_LIST.map((item: CheckboxItem) => (
            <CheckboxMui
              testID={item.dataKey}
              key={item.id}
              value={item.value}
              label={translate(item.labelLocaleKey)}
              checked={
                filters?.compensationType?.[
                  item.dataKey as keyof typeof filters.compensationType
                ]
              }
              onChange={(event) => {
                const value = {
                  ...(filters?.compensationType || {}),
                  [item.dataKey]: event.target.checked,
                };
                updateFilters('compensationType', value);
              }}
            />
          ))}
        </ResponsiveStack>

        <Stack direction="column">
          <FilterRangeSlider
            showOnMax
            maxSign="+"
            label={translate('How much do you want to be paid?')}
            name="compensationSum"
            curMin={
              filters.compensationSum?.curMin || DEFAULT_MIN_COMPENSATION_SUM
            }
            curMax={
              filters.compensationSum?.curMax || DEFAULT_MAX_COMPENSATION_SUM
            }
            value={[
              filters.compensationSum?.curMin || DEFAULT_MIN_COMPENSATION_SUM,
              filters.compensationSum?.curMax || DEFAULT_MAX_COMPENSATION_SUM,
            ]}
            min={filters.compensationSum?.min || DEFAULT_MIN_COMPENSATION_SUM}
            max={filters.compensationSum?.max || DEFAULT_MAX_COMPENSATION_SUM}
            onChange={(minMaxObject) => {
              updateFilters('compensationSum', minMaxObject);
            }}
            prefix="$"
          />
        </Stack>
      </SectionWithBorder>

      <SectionWithoutBottomMargin>
        <SectionTagsContainer isOpen={isVerticalTagsOpen}>
          <SectionHeader>
            <SectionHeaderTitle>{translate('Tags')}</SectionHeaderTitle>
          </SectionHeader>

          <SectionTagsBase>
            <Conditional condition={isLoadingVerticalTags}>
              <Box>{translate('Loading...')}</Box>
            </Conditional>
            {verticalTags?.map((tag: {name: string; displayText: string}) => {
              return (
                <ToggleButtonMui
                  type="tags"
                  testID={`tags-${tag.name}`}
                  key={tag.name}
                  value={tag.name}
                  selected={filters?.tags?.includes(tag.name)}
                  onChange={(event, key) => {
                    const value = filters?.tags?.includes(tag.name)
                      ? filters?.tags?.filter(
                          (item: string) => item !== tag.name
                        )
                      : [...(filters?.tags || []), tag.name];

                    updateFilters('tags', value);
                  }}
                >
                  {tag.displayText}
                </ToggleButtonMui>
              );
            })}
          </SectionTagsBase>
        </SectionTagsContainer>
        <SectionShowMoreButton direction="row">
          <ButtonMui
            onClick={() => setIsVerticalTagsOpen(!isVerticalTagsOpen)}
            testID="toggle_tags_show"
          >
            {isVerticalTagsOpen
              ? translate('Show less')
              : translate('Show more')}
          </ButtonMui>
        </SectionShowMoreButton>
      </SectionWithoutBottomMargin>
    </>
  );
}

export default GigsFiltersPopupCanvas;
